import {
  and,
  desc,
  eq,
  post,
  post_comment,
  post_like,
  post_report,
  type PowerSyncDrizzleDB,
} from "@famidex/powersync";
import { liveCommentFilters, livePostFilters } from "../live/filters";

/**
 * 📣 COMUNIDAD — lecturas del cliente (local-first). El feed público [H] llega
 * por el stream `community_feed`; los reportes [L] por `community_reports`. Las
 * publicaciones y comentarios llevan papelera: TODA lectura viva pasa por la capa
 * live (`livePostFilters`/`liveCommentFilters`). El invitado sin token de sync
 * ojea el feed por REST (`/api/community/*`).
 */

/** Feed cronológico público: publicaciones vivas, más recientes primero. */
export function getFeedQuery(db: PowerSyncDrizzleDB, limit = 100) {
  return db
    .select()
    .from(post)
    .where(and(eq(post.visibility, "public"), ...livePostFilters()))
    .orderBy(desc(post.created_at))
    .limit(limit);
}

/**
 * Detalle de UNA publicación por id (lookup id-scoped, `keyed`: ver una fila
 * borrada es inocuo — la resuelve el estado not-found de la ruta).
 */
export function getPostByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(post).where(eq(post.id, id)).limit(1);
}

/** Publicaciones de un perfil (pestaña «publicaciones»), vivas y recientes. */
export function getUserPostsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(post)
    .where(and(eq(post.user_id, userId), ...livePostFilters()))
    .orderBy(desc(post.created_at));
}

/**
 * GALERÍA COMUNITARIA de un producto: publicaciones vivas etiquetadas con él
 * (slot aditivo del detalle del producto). Sólo públicas.
 */
export function getProductGalleryQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(post)
    .where(and(eq(post.product_id, productId), eq(post.visibility, "public"), ...livePostFilters()))
    .orderBy(desc(post.created_at));
}

/** Comentarios vivos de una publicación, más antiguos primero (orden de hilo). */
export function getPostCommentsQuery(db: PowerSyncDrizzleDB, postId: string) {
  return db
    .select()
    .from(post_comment)
    .where(and(eq(post_comment.post_id, postId), ...liveCommentFilters()))
    .orderBy(post_comment.created_at);
}

/**
 * TODOS los likes sincronizados (feed acotado): la UI deriva client-side el
 * contador por publicación y si el usuario ya likeó. `post_like` NO es
 * soft-delete (quitar el like es hard-delete), así que no pasa por la capa live.
 */
export function getFeedLikesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(post_like);
}

/**
 * `post_id` de TODOS los comentarios vivos (feed acotado): la UI deriva el
 * contador de comentarios por publicación con `countByPostId`. Vivo (papelera
 * fuera) vía `liveCommentFilters`.
 */
export function getFeedCommentsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({ post_id: post_comment.post_id })
    .from(post_comment)
    .where(and(...liveCommentFilters()));
}

/** Comentarios/likes de UNA publicación (contador exacto en el detalle). */
export function getPostLikesQuery(db: PowerSyncDrizzleDB, postId: string) {
  return db.select().from(post_like).where(eq(post_like.post_id, postId));
}

/**
 * Lookup del like del usuario sobre una publicación (base del toggle). Idempotente:
 * SELECT existente → si hay, se quita (DELETE); si no, se crea (INSERT).
 */
export function getUserLikeForPostQuery(db: PowerSyncDrizzleDB, postId: string, userId: string) {
  return db
    .select()
    .from(post_like)
    .where(and(eq(post_like.post_id, postId), eq(post_like.user_id, userId)))
    .limit(1);
}

/**
 * Reporte previo del usuario sobre una publicación (idempotencia del formulario:
 * un reporte por usuario y publicación). `post_report` es privado del reporter.
 */
export function getUserReportForPostQuery(db: PowerSyncDrizzleDB, postId: string, userId: string) {
  return db
    .select()
    .from(post_report)
    .where(and(eq(post_report.post_id, postId), eq(post_report.user_id, userId)))
    .limit(1);
}
